/**
 * Shared IROP (Irregular Operations) utilities for AETHER.
 */
import { UnscheduledEvent } from '../types';
import { IROP_DISPLAY, IROP_FLAGS } from '../constants';

/** A resolved IROP display entry: the event flag key and its shown label. */
export interface IropDisplayEntry {
  flag: keyof UnscheduledEvent;
  label: string;
}

/**
 * Returns the active IROP display entries for an event, in the business-defined
 * display order (see IROP_DISPLAY). Each entry is rendered on its own centered
 * line in the homepage IROP column.
 */
export function getIropDisplayEntries(event: UnscheduledEvent): IropDisplayEntry[] {
  return IROP_DISPLAY.filter((rule) => Boolean(event[rule.flag])).map((rule) => ({
    flag: rule.flag,
    label: rule.label,
  }));
}

/**
 * Returns an array of active IROP flag short codes for an event.
 */
export function getIropFlags(event: UnscheduledEvent): string[] {
  const flags: string[] = [];
  if (event.returnToBlock) flags.push('RTB');
  if (event.abortedTakeOff) flags.push('ATO');
  if (event.airTurnBack) flags.push('ATB');
  if (event.diversion) flags.push('DIV');
  if (event.cancellation) flags.push('CXL');
  if (event.delay) flags.push('DLY');
  if (event.damage) flags.push('DMG');
  return flags;
}

/**
 * Collects the distinct set of IROP codes present across a list of events.
 * Returns a sorted array of unique IROP short codes.
 */
export function collectIropOptions(events: UnscheduledEvent[]): string[] {
  const irops = new Set<string>();
  for (const event of events) {
    if (event.returnToBlock) irops.add('RTB');
    if (event.abortedTakeOff) irops.add('ATO');
    if (event.airTurnBack) irops.add('ATB');
    if (event.diversion) irops.add('DIV');
    if (event.cancellation) irops.add('CXL');
    if (event.delay) irops.add('DLY');
    if (event.damage) irops.add('DMG');
  }
  return Array.from(irops).sort();
}

/** An IROP filter dropdown entry: short code, human label, and event flag. */
export interface IropFilterOption {
  code: string;
  label: string;
  flag: keyof UnscheduledEvent;
}

/** Short IROP code (key of IROP_FLAGS). */
type IropCode = keyof typeof IROP_FLAGS;

/** Human-readable IROP labels keyed by short code, for filter dropdowns. */
const IROP_LABELS: Record<IropCode, string> = {
  RTB: 'Return to Block',
  ATO: 'Aborted Take Off',
  ATB: 'Air Turn Back',
  DIV: 'Diversion',
  CXL: 'Cancellation',
  DLY: 'Delay',
  DMG: 'Damage',
};

/**
 * IROP filter dropdown entries in business-defined display order. Derived from
 * IROP_FLAGS (single source of truth for the code→event-flag mapping) so the
 * codes never drift from the underlying event fields.
 */
const IROP_FILTER_ENTRIES: readonly IropFilterOption[] = (
  ['DLY', 'CXL', 'DMG', 'DIV', 'RTB', 'ATO', 'ATB'] as IropCode[]
).map((code) => ({ code, label: IROP_LABELS[code], flag: IROP_FLAGS[code] }));

/** True when `code` is a known IROP short code (e.g. 'RTB', 'DLY'). */
export function isIropCode(code: string): boolean {
  return IROP_FILTER_ENTRIES.some((entry) => entry.code === code);
}

/** True when the event has the IROP identified by `code` set. */
export function eventHasIropCode(event: UnscheduledEvent, code: string): boolean {
  const entry = IROP_FILTER_ENTRIES.find((option) => option.code === code);
  return entry ? Boolean(event[entry.flag]) : false;
}

/**
 * Collects the distinct IROP filter options present across `events`, in display
 * order. Used to populate the History IROP filter with only the IROP types that
 * actually appear in the listed records.
 */
export function collectIropFilterOptions(events: UnscheduledEvent[]): IropFilterOption[] {
  return IROP_FILTER_ENTRIES.filter((entry) => events.some((event) => Boolean(event[entry.flag])));
}
